'use client';

/**
 * Pure helpers, types, and presentational sub-components for `ExperienceStep`.
 * Extracted from `experience-step.tsx` (conformance, code-size guard) so the
 * main component stays a single stateful surface and these dependency-free
 * pieces live next to it.
 *
 * Nothing here owns experience state — the helpers are pure functions, and the
 * card components receive their data + callbacks as props. The canonical
 * add/edit/delete flow stays in `ExperienceStep` (single edit surface,
 * Critical Rule #8).
 */
import type { TFunction } from 'i18next';
import {
  Award,
  Building2,
  GraduationCap,
  Pencil,
  Trash2,
  Trophy,
} from 'lucide-react';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card } from '@kit/ui/card';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import type { ExperienceType as DbExperienceType } from '../schemas/experience.schema';
import type { OrganizationSummary } from './organization-types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * UI-facing experience type.
 *
 * Note: the type space includes the legacy profile-setup values
 * ('school', 'company', 'other') AND the newer DB CHECK values
 * ('award', 'performance', 'education', 'teaching', 'workshop',
 * 'competition'). Both are accepted by the underlying CHECK constraint
 * and by `ExperienceTypeSchema`.
 */
export type ExperienceType = DbExperienceType;

export interface Organization {
  id: string;
  name: string;
  organization_type?: string | null;
}

export interface Rank {
  id: string;
  name: { en: string; fr?: string };
  category: string;
  year?: number;
}

export interface Experience {
  id: string;
  dancer_id?: string | null;
  organization_id?: string | null;
  organization_manual?: string | null;
  organization_manual_type?: string | null;
  type?: string | null;
  rank_id?: string | null;
  rank_title?: string | null;
  placement?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  current?: boolean | null;
  graduated?: boolean | null;
  verified?: boolean | null;
  hidden_on_candidate?: boolean | null;
  hide_on_resume?: boolean | null;
  score?: number | null;
  title?: string | null;
  description?: string | null;
  meteor_id?: string | null;
  legacy_data?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  organization?: {
    id: string;
    name: string;
    organization_type: string | null;
  } | null;
}

export const EXPERIENCE_CATEGORY_TYPES: ReadonlyArray<string> = [
  'school',
  'company',
  'performance',
  'education',
  'teaching',
  'workshop',
  'competition',
  'other',
];

export const AWARD_CATEGORY_TYPES: ReadonlyArray<string> = ['award'];

// =============================================================================
// HELPERS
// =============================================================================

export const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Format a `YYYY-MM-DD` (or `YYYY-MM`) date string as `MMM YYYY` without
 * routing through `new Date()` — avoids the timezone shift bug per
 * Critical Rule #19 (a UTC-stored `2024-01-01` would render as `Dec 2023`
 * for users west of UTC if parsed via `new Date()`).
 */
export function formatMonthYear(value: string): string {
  const match = /^(\d{4})-(0[1-9]|1[0-2])/.exec(value);
  if (!match) return value;
  const year = match[1];
  const monthIdx = Number.parseInt(match[2]!, 10) - 1;
  return `${MONTH_LABELS[monthIdx]} ${year}`;
}

/** UI form-type for the inline add/edit card. */
export type FormType = 'school' | 'company' | 'other' | 'award';

export function isFormType(value: string): value is FormType {
  return (
    value === 'school' ||
    value === 'company' ||
    value === 'other' ||
    value === 'award'
  );
}

/**
 * The organization type space (school/company/course/competition/foundation/
 * brand/service) is richer than the form-type space (school/company/other/
 * award). Keep these mappings centralized so the form-vs-org-type contract
 * is auditable in one place.
 */
export type OrganizationTypeForBackfill =
  | 'school'
  | 'company'
  | 'course'
  | 'competition'
  | 'foundation'
  | 'brand'
  | 'service';

/**
 * Map a UI form-type to the closest organization_type value to seed a new
 * draft org row with. Awards and `other` (= competition) both map to
 * `competition` because that's the existing convention in
 * `organization_manual_type` writes elsewhere in this component.
 */
export function formTypeToOrgType(t: FormType): OrganizationTypeForBackfill {
  switch (t) {
    case 'school':
      return 'school';
    case 'company':
      return 'company';
    case 'other':
    case 'award':
    default:
      return 'competition';
  }
}

/**
 * Map an organization_type from the DB to the UI's form-type space, used
 * to lock the Type select when the dancer picks a known org. The select
 * only renders three options (school/company/other), so all the rare
 * organization_type values (course/foundation/brand/service) collapse to
 * `other`. The competition slot is also `other` (the existing UX
 * taxonomy).
 */
export function orgTypeToFormType(
  orgType: string | null | undefined,
): FormType {
  switch (orgType) {
    case 'school':
      return 'school';
    case 'company':
      return 'company';
    default:
      return 'other';
  }
}

/**
 * Maps the canonical `OrganizationSummary` (the shape `OrganizationPicker`
 * hands back via `onSelect` / `onCreate`) to the narrower `Organization`
 * shape this component's state and payload builders use. `Organization`
 * predates `OrganizationSummary` and only needs `id` / `name` /
 * `organization_type` for the experience-form logic (type-lock, rank
 * loading, submit payload) — this mapper is the single seam between the two.
 */
export function organizationSummaryToOrganization(
  o: OrganizationSummary,
): Organization {
  return {
    id: o.id,
    name: o.name,
    organization_type: o.organizationType ?? null,
  };
}

/**
 * Reverse of {@link organizationSummaryToOrganization} — used to seed
 * `OrganizationPicker`'s `initialSelected` from the form's already-hydrated
 * `Organization` (edit mode). `slug` / `handle` / `verified` / `logoUrl`
 * aren't tracked by the narrower `Organization` shape, so the picker falls
 * back to its own resolved values for those once a live search result
 * supersedes this seed.
 */
export function organizationToSummary(o: Organization): OrganizationSummary {
  return {
    id: o.id,
    name: o.name,
    organizationType: o.organization_type ?? null,
  };
}

/** Get icon for experience type. */
export function getTypeIcon(type?: string | null) {
  switch (type) {
    case 'school':
      return <GraduationCap className="h-4 w-4" />;
    case 'company':
      return <Building2 className="h-4 w-4" />;
    case 'award':
      return <Award className="h-4 w-4" />;
    default:
      return <Trophy className="h-4 w-4" />;
  }
}

/** Get badge variant for type. */
export function getTypeBadgeVariant(type?: string | null) {
  switch (type) {
    case 'school':
      return 'secondary' as const;
    case 'company':
      return 'default' as const;
    default:
      return 'outline' as const;
  }
}

/**
 * Resolve organization name. After 20260504093000 the data layer
 * guarantees one of these branches resolves: typeahead-selected orgs
 * come back via the join, dancer-typed names live on
 * `organization_manual` (the save path persists it; the migration
 * backfilled pre-existing rows). The empty-string fallback exists so
 * any future regression surfaces as a visibly missing name in the UI
 * rather than a friendly-but-misleading "Unknown" placeholder.
 */
export function getOrgDisplayName(exp: Experience): string {
  return exp.organization?.name ?? exp.organization_manual ?? '';
}

// =============================================================================
// PRESENTATIONAL CARDS (profile mode)
// =============================================================================

interface ExperienceCardProps {
  exp: Experience;
  isPending: boolean;
  editingId: string | null;
  actorRole: 'self' | 'admin';
  /** When true, hide the edit/delete affordances entirely (pure display). */
  readOnly?: boolean;
  /** i18n function from the parent's `useTranslation('dancer')`. */
  t: TFunction<'dancer'>;
  onEdit: (exp: Experience) => void;
  onDelete: (id: string) => void;
}

/**
 * Read-only card for a single school/company/competition experience in the
 * profile-mode list. Edit + delete affordances delegate to the parent's
 * handlers.
 */
export function ExperienceCard({
  exp,
  isPending,
  editingId,
  actorRole,
  readOnly = false,
  t,
  onEdit,
  onDelete,
}: ExperienceCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-1">{getTypeIcon(exp.type)}</div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{getOrgDisplayName(exp)}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge variant={getTypeBadgeVariant(exp.type)}>
                {exp.type === 'school' &&
                  t('profileSetup.experience.typeSchool', 'School')}
                {exp.type === 'company' &&
                  t('profileSetup.experience.typeCompany', 'Company')}
                {exp.type === 'other' &&
                  t('profileSetup.experience.typeCompetition', 'Competition')}
                {exp.type !== 'school' &&
                  exp.type !== 'company' &&
                  exp.type !== 'other' &&
                  exp.type}
              </Badge>
              <If condition={!!exp.rank_title}>
                <Badge variant="outline">{exp.rank_title}</Badge>
              </If>
              <If condition={!!exp.current}>
                <Badge variant="secondary">
                  <Trans i18nKey="dancer:profileSetup.experience.current" />
                </Badge>
              </If>
              <If condition={!!exp.graduated}>
                <Badge variant="secondary">
                  <Trans i18nKey="dancer:profileSetup.experience.graduated" />
                </Badge>
              </If>
            </div>
            <If condition={!!exp.start_date || !!exp.end_date}>
              <div className="text-muted-foreground mt-1 text-sm">
                {exp.start_date && formatMonthYear(exp.start_date)}
                {exp.start_date && (exp.end_date || exp.current) && ' – '}
                {exp.current && t('profileSetup.experience.present', 'Present')}
                {!exp.current && exp.end_date && formatMonthYear(exp.end_date)}
              </div>
            </If>
          </div>
        </div>
        {!readOnly && (
          <div className="flex shrink-0 items-start gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(exp)}
              disabled={isPending || editingId === exp.id}
              aria-label={t(
                'profileSetup.experience.editExperience',
                'Edit experience',
              )}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {actorRole !== 'admin' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(exp.id)}
                disabled={isPending}
                aria-label={t(
                  'profileSetup.experience.deleteExperience',
                  'Delete experience',
                )}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

interface AwardCardProps {
  award: Experience;
  isPending: boolean;
  editingId: string | null;
  actorRole: 'self' | 'admin';
  /** When true, hide the edit/delete affordances entirely (pure display). */
  readOnly?: boolean;
  t: TFunction<'dancer'>;
  onEdit: (exp: Experience) => void;
  onDelete: (id: string) => void;
}

/** Read-only card for a single award/achievement in the profile-mode list. */
export function AwardCard({
  award,
  isPending,
  editingId,
  actorRole,
  readOnly = false,
  t,
  onEdit,
  onDelete,
}: AwardCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-1">
            <Award className="h-4 w-4 text-amber-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">
              {getOrgDisplayName(award)}
            </div>
            <div className="text-muted-foreground mt-1 text-xs">
              {award.title && `${award.title} · `}
              {award.start_date && award.start_date.slice(0, 4)}
            </div>
          </div>
          <If condition={!!award.placement}>
            <Badge variant="secondary">{award.placement}</Badge>
          </If>
        </div>
        {!readOnly && (
          <div className="flex shrink-0 items-start gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(award)}
              disabled={isPending || editingId === award.id}
              aria-label={t('profileSetup.experience.editAward', 'Edit award')}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {actorRole !== 'admin' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(award.id)}
                disabled={isPending}
                aria-label={t(
                  'profileSetup.experience.deleteAward',
                  'Delete award',
                )}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
